/** Tiny type-safe DOM helpers — keeps us from innerHTML for XSS safety. */

type AttrValue = string | number | boolean | undefined | null | EventListener;
type AttrMap = Partial<Record<string, AttrValue>>;
type Child = Node | string | null | undefined | false;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs?: AttrMap,
  children?: Child | Child[],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      if (v === undefined || v === null || v === false) continue;
      if (k.startsWith('on') && typeof v === 'function') {
        node.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
        continue;
      }
      if (typeof v === 'function') continue;
      if (k === 'class') node.className = String(v);
      else if (v === true) node.setAttribute(k, '');
      else node.setAttribute(k, String(v));
    }
  }
  appendAll(node, children);
  return node;
}

export function appendAll(parent: Node, children: Child | Child[] | undefined): void {
  if (children === undefined || children === false || children === null) return;
  const list = Array.isArray(children) ? children : [children];
  for (const c of list) {
    if (c === undefined || c === null || c === false) continue;
    if (typeof c === 'string') parent.appendChild(document.createTextNode(c));
    else parent.appendChild(c);
  }
}

export function clear(node: Node) {
  while (node.firstChild) node.removeChild(node.firstChild);
}

export function mount(root: HTMLElement, view: HTMLElement): void {
  clear(root);
  root.appendChild(view);
}

/**
 * SVG-namespaced element helper. Needed for inline icons/logos: HTMLElement
 * created via document.createElement('svg') would render as unknown HTML and
 * the inner shapes would not paint. Only `setAttribute` is used; no event
 * listeners, no dangerous props — this is purely presentational.
 */
export function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs?: Record<string, string | number>,
  children?: Child | Child[],
): SVGElementTagNameMap[K] {
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const node = document.createElementNS(SVG_NS, tag);
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      if (v === undefined || v === null) continue;
      node.setAttribute(k, String(v));
    }
  }
  appendAll(node, children);
  return node;
}
